import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import enableRpcWarningIcon from '@/assets/figma/rpc/enable-rpc-warning.svg';
import { useWalletSignature } from '@/app/hooks/useWalletSignature';
import { enableTronEnergy, getEnableTronEnergyChallenge } from '@/api';

export interface EnableTronEnergyModalProps {
  isOpen: boolean;
  appId: string | null;
  appName?: string;
  onClose: () => void;
  onSuccess: (appId: string) => void;
}

export function EnableTronEnergyModal({
  isOpen,
  appId,
  onClose,
  onSuccess,
}: EnableTronEnergyModalProps) {
  const { t } = useTranslation();
  const { signMessage } = useWalletSignature();
  const [isSigning, setIsSigning] = useState(false);

  if (!isOpen || !appId) return null;

  const handleEnable = async () => {
    setIsSigning(true);
    toast.loading(t('tronEnergy.modal.signing', '等待钱包签名...'), { id: 'enable-tron-energy' });

    try {
      const challengeRes = await getEnableTronEnergyChallenge(appId);
      if (!challengeRes.ok) {
        toast.error(t('tronEnergy.modal.challengeFailed', '获取签名挑战失败'), {
          id: 'enable-tron-energy',
        });
        setIsSigning(false);
        return;
      }

      const signatureResult = await signMessage(challengeRes.data.message);
      if (!signatureResult) {
        toast.error(t('tronEnergy.modal.signatureRejected', '已取消签名或签名失败'), {
          id: 'enable-tron-energy',
        });
        setIsSigning(false);
        return;
      }

      const enableRes = await enableTronEnergy({
        appId,
        challengeId: challengeRes.data.challengeId,
        signature: signatureResult.signature,
      });
      if (!enableRes.ok) {
        toast.error(t('tronEnergy.modal.enableFailed', '启用服务失败，请重试'), {
          id: 'enable-tron-energy',
        });
        setIsSigning(false);
        return;
      }

      toast.success(t('tronEnergy.modal.enableSuccess', 'Tron 能量租赁已启用'), {
        id: 'enable-tron-energy',
      });
      setIsSigning(false);
      onSuccess(appId);
    } catch (error) {
      console.error('[EnableTronEnergyModal] Error:', error);
      toast.error(t('tronEnergy.modal.enableFailed', '启用服务失败，请重试'), {
        id: 'enable-tron-energy',
      });
      setIsSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-[486px] flex flex-col gap-8 items-center overflow-hidden rounded-3xl border border-slate-200 dark:border-[#1d293d] bg-white dark:bg-[rgba(210,240,225,0.05)] p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="w-full flex flex-col gap-6 items-center">
          <div className="w-full flex flex-col gap-4 items-center">
            <div className="relative shrink-0 size-[80px]">
              <img
                src={enableRpcWarningIcon}
                alt=""
                width={80}
                height={80}
                className="block size-full"
              />
            </div>
            <div className="w-full flex flex-col gap-2.5 items-center justify-center px-12 text-center">
              <h3 className="w-full text-xl font-bold leading-[1.5] text-slate-900 dark:text-white">
                {t('tronEnergy.modal.enableTitle', '启用 Tron 能量租赁')}
              </h3>
              <p className="w-full text-sm font-normal leading-[1.5] text-slate-500 dark:text-[#90a1b9]">
                {t('keys.rpcEnableHint')}
              </p>
            </div>
          </div>
          <div className="w-full flex items-center justify-center rounded-xl border border-slate-200 dark:border-[#1d293d] bg-slate-50 dark:bg-[#0e0f11] p-3">
            <p className="text-base font-normal leading-[1.5] text-center text-slate-500 dark:text-[#90a1b9]">
              {t('keys.rpcEnableHint')}
            </p>
          </div>
        </div>
        <div className="w-full flex gap-4 items-center">
          <button
            type="button"
            onClick={onClose}
            disabled={isSigning}
            className="flex-1 min-w-0 flex items-center justify-center rounded-full border border-slate-300 dark:border-[#1d293d] bg-slate-100 dark:bg-[rgba(210,240,225,0.05)] p-4 text-base font-normal text-slate-900 dark:text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleEnable}
            disabled={isSigning}
            className="flex-1 min-w-0 flex items-center justify-center gap-2 rounded-full border border-slate-300 dark:border-[#1d293d] bg-emerald-500 dark:bg-[#00FFA3] p-4 text-base font-normal text-white dark:text-[#030e12] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSigning && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('keys.rpcEnableConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
